import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { CompletionShell } from "@/components/pages/SuccessPage";
import { hooks as h } from "@/hooks";
import { diariesCreatedSinceCount } from "@/features/invite/api/diariesCreatedSinceCount";
import { isUuid } from "@/services/isUuid";
import { supabaseClient } from "@/lib/supabase";
import {
    type DiaryInviteSearchT,
    getDiaryInviteContext,
    getDiaryInviteOrigin,
} from "@/features/invite/utils/diaryInviteContext";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { useAuth } from "@/utils/auth";

type InviteCompleteSearch = DiaryInviteSearchT & {
    diary_id?: string;
    invited_patient_email?: string;
    invited_patient_phone?: string;
};

function inviteSearchFromComplete(search: InviteCompleteSearch): DiaryInviteSearchT {
    const {
        diary_id: _diaryId,
        invited_patient_email: _email,
        invited_patient_phone: _phone,
        ...inviteSearch
    } = search;
    return inviteSearch;
}

export const Route = createFileRoute("/_app/invite/diary/complete")({
    component: CompletePage,
    validateSearch: (search: Record<string, unknown>): InviteCompleteSearch => search as InviteCompleteSearch,
});

function CompletePage() {
    const auth = useAuth();
    const search = Route.useSearch();
    const { diary_id, invited_patient_email, invited_patient_phone } = search;
    const hasDiary = Boolean(diary_id && isUuid(diary_id));
    const inviteSearch = inviteSearchFromComplete(search);

    const inviteCopy = useInviteCopy();
    const patientsCopy = usePatientsCopy();
    const templatesCopy = useTemplatesCopy();

    const origin = getDiaryInviteOrigin(search);
    const context = getDiaryInviteContext(origin, {
        patients: patientsCopy.patients,
        templates: templatesCopy.templates,
        newDiary: patientsCopy.newDiary,
    });

    h.useNav([
        { label: context.rootLabel, route: { to: context.rootTo } },
        { label: context.flowLabel, route: { to: "/invite/diary/review", search: inviteSearch } },
        { label: inviteCopy.inviteSent, route: { to: "/invite/diary/complete", search } },
    ]);

    // Soft-load only: never block the success UI / CTAs on this secondary count.
    // Counts diaries you created in the last 7 days (not the patients dashboard total).
    // Only bump +1 if the just-sent diary is not in the result yet (race with cache).
    const { data: diariesCount } = useQuery({
        queryKey: ["diaries-last-7-days", auth.id, diary_id],
        queryFn: async () => {
            const sevenDaysAgo = dayjs().subtract(7, "day").toISOString();
            const { data, error } = await supabaseClient.rpc("count_my_diaries_created_since", {
                p_since: sevenDaysAgo,
                p_diary_id: diary_id && isUuid(diary_id) ? diary_id : undefined,
            });

            if (error) throw error;
            return diariesCreatedSinceCount(data, diary_id);
        },
        enabled: Boolean(auth.id),
        staleTime: 30_000,
    });

    const invitedContact = invited_patient_email || invited_patient_phone;
    const patientLine = invited_patient_email
        ? inviteCopy.patientEmailWillReceiveInstructions(invited_patient_email)
        : invited_patient_phone
          ? inviteCopy.patientPhoneWillReceiveInstructions(invited_patient_phone)
          : inviteCopy.patientWillReceiveInstructions;

    const diaryCountLine = typeof diariesCount === "number" ? inviteCopy.diariesAddedInLast7Days(diariesCount) : "";

    const description = diaryCountLine ? `${patientLine}\n${diaryCountLine}` : patientLine;
    const title = invitedContact ? inviteCopy.inviteSentTo(invitedContact) : inviteCopy.inviteSent;

    return (
        <CompletionShell
            title={title}
            statusLabel={title}
            description={description}
            pinFooter
            isLoading={false}
            minLoadingTime={0}
            primaryButton={
                hasDiary
                    ? {
                          text: inviteCopy.goToDiary,
                          to: "/patients/$diaryId",
                          params: { diaryId: diary_id! },
                          search: { tab: "pending" },
                      }
                    : {
                          text: inviteCopy.inviteAnotherPatient,
                          to: "/invite/diary/review",
                          search: inviteSearch,
                      }
            }
            secondaryButton={
                hasDiary
                    ? {
                          text: inviteCopy.inviteAnotherPatient,
                          to: "/invite/diary/review",
                          search: inviteSearch,
                          showWhen: true,
                      }
                    : undefined
            }
        />
    );
}
